import { useState } from 'react';
import {
  LayoutDashboard, Calendar, Users, FileText, Settings, Bell, Search, Plus,
  ChevronLeft, ChevronRight, Stethoscope, MoreHorizontal, TrendingUp,
  Clock, MessageCircle, Activity, DollarSign
} from 'lucide-react';

const FONT_LINK = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Manrope:wght@400;500;600;700&family=Instrument+Serif&display=swap');
    .font-geist { font-family: 'Geist', system-ui, sans-serif; }
    .font-mono { font-family: 'JetBrains Mono', monospace; }
    .font-manrope { font-family: 'Manrope', system-ui, sans-serif; }
    .font-serif-italic { font-family: 'Instrument Serif', serif; font-style: italic; }
  `}</style>
);

// =================================================================
// THEME A — "Graphite Pulse" : preto + amarelo elétrico (1 accent)
// Inspired by: Vercel + Linear + Notion dark accents
// =================================================================
const THEME_A = {
  name: 'Graphite Pulse',
  desc: 'Quase preto + acento amarelo elétrico. Apenas 1 cor, máximo contraste, vibe tech moderna.',
  bg: 'bg-[#FAFAFA]',
  surface: 'bg-white',
  ink: '#0A0A0A',
  inkSoft: '#525252',
  border: '#E5E5E5',
  borderSoft: '#F5F5F5',
  accent: '#D4FF00',
  accentInk: '#FAFAFA',
  primary: '#0A0A0A',
  primaryHover: '#262626',
  font: 'font-geist',
  // status simplificados (cinzas + 1 accent)
  status: {
    confirmada: { dot: '#0A0A0A', label: 'text-[#0A0A0A]' },
    agendada: { dot: '#A3A3A3', label: 'text-[#525252]' },
    realizada: { dot: '#D4D4D4', label: 'text-[#A3A3A3]' },
    cancelada: { dot: '#FCA5A5', label: 'text-[#525252] line-through' },
  },
  tipo: {
    presencial: { stripe: 'bg-[#0A0A0A]', label: 'PRESENCIAL' },
    online: { stripe: 'bg-[#525252]', label: 'ONLINE' },
    retorno: { stripe: 'bg-[#D4FF00]', label: 'RETORNO' },
  },
};

// =================================================================
// THEME B — "Stone Lab" : grayscale + um azul-gelo sóbrio
// Inspired by: Stripe + Apple Health
// =================================================================
const THEME_B = {
  name: 'Stone Lab',
  desc: 'Escala monocromática + azul-gelo discreto. Visual clean tipo Stripe/Apple Health, ultra profissional.',
  bg: 'bg-[#FAFAF9]',
  surface: 'bg-white',
  ink: '#171717',
  inkSoft: '#737373',
  border: '#E7E5E4',
  borderSoft: '#F5F5F4',
  accent: '#3B82F6',
  accentInk: '#FFFFFF',
  primary: '#171717',
  primaryHover: '#000000',
  font: 'font-manrope',
  status: {
    confirmada: { dot: '#3B82F6', label: 'text-[#1E40AF]' },
    agendada: { dot: '#A8A29E', label: 'text-[#57534E]' },
    realizada: { dot: '#D6D3D1', label: 'text-[#A8A29E]' },
    cancelada: { dot: '#FCA5A5', label: 'text-[#A8A29E] line-through' },
  },
  tipo: {
    presencial: { stripe: 'bg-[#3B82F6]', label: 'Presencial' },
    online: { stripe: 'bg-[#171717]', label: 'Online' },
    retorno: { stripe: 'bg-[#A8A29E]', label: 'Retorno' },
  },
};

// =================================================================
// THEME C — "Coral Editorial" : off-black + um único coral sutil
// Inspired by: MoMA / Apple Marketing / Editorial fashion
// =================================================================
const THEME_C = {
  name: 'Coral Editorial',
  desc: 'Off-black + coral discreto + serifa em destaques. Aspecto editorial e luxuoso, tipo MoMA/Apple Keynote.',
  bg: 'bg-[#FAF7F5]',
  surface: 'bg-white',
  ink: '#1A1A1A',
  inkSoft: '#737373',
  border: '#EDE9E5',
  borderSoft: '#F5F1ED',
  accent: '#FF6B6B',
  accentInk: '#FFFFFF',
  primary: '#1A1A1A',
  primaryHover: '#333333',
  font: 'font-geist',
  status: {
    confirmada: { dot: '#1A1A1A', label: 'text-[#1A1A1A]' },
    agendada: { dot: '#FF6B6B', label: 'text-[#FF6B6B]' },
    realizada: { dot: '#D4D4D4', label: 'text-[#A3A3A3]' },
    cancelada: { dot: '#A3A3A3', label: 'text-[#A3A3A3] line-through' },
  },
  tipo: {
    presencial: { stripe: 'bg-[#1A1A1A]', label: 'Presencial' },
    online: { stripe: 'bg-[#737373]', label: 'Online' },
    retorno: { stripe: 'bg-[#FF6B6B]', label: 'Retorno' },
  },
};

// =================== Generic helpers ===================
const Avatar = ({ name, theme, size = 'md' }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const sizes = { sm: 'w-7 h-7 text-[10px]', md: 'w-9 h-9 text-xs', lg: 'w-12 h-12 text-base' };
  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}
      style={{ background: theme.borderSoft, color: theme.ink }}
    >
      {initials}
    </div>
  );
};

const Sidebar = ({ active, theme }) => {
  const items = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { key: 'agenda', icon: Calendar, label: 'Agenda' },
    { key: 'pacientes', icon: Users, label: 'Pacientes' },
    { key: 'prontuarios', icon: FileText, label: 'Prontuários' },
    { key: 'configuracoes', icon: Settings, label: 'Configurações' },
  ];
  return (
    <aside className={`w-60 ${theme.surface} border-r flex flex-col flex-shrink-0`} style={{ borderColor: theme.border }}>
      <div className="p-5 border-b" style={{ borderColor: theme.border }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: theme.primary }}>
            <Stethoscope className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: theme.accentInk }} />
          </div>
          <div>
            <div className={`${theme.font} text-[15px] font-semibold leading-none tracking-tight`} style={{ color: theme.ink }}>Agenda Mais</div>
            <div className="text-[9px] tracking-[0.2em] uppercase mt-1" style={{ color: theme.inkSoft }}>Med</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-2.5 space-y-0.5">
        {items.map(it => {
          const Icon = it.icon;
          const isActive = it.key === active;
          return (
            <a key={it.key} href="#"
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-all`}
              style={isActive
                ? { background: theme.primary, color: theme.accentInk }
                : { color: theme.ink }}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {it.label}
            </a>
          );
        })}
      </nav>
      <div className="p-2.5 border-t" style={{ borderColor: theme.border }}>
        <div className="flex items-center gap-2 p-1.5 rounded-md">
          <Avatar name="Nathalie Sanches" theme={theme} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold truncate" style={{ color: theme.ink }}>Dra. Nathalie</div>
            <div className="text-[10px] truncate" style={{ color: theme.inkSoft }}>Ginecologia</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const TopBar = ({ title, subtitle, theme }) => (
  <header className={`${theme.surface} border-b px-7 py-4 flex items-center justify-between flex-shrink-0`} style={{ borderColor: theme.border }}>
    <div>
      <h1 className={`${theme.font} text-xl font-semibold tracking-tight`} style={{ color: theme.ink }}>{title}</h1>
      {subtitle && <p className="text-[12px] mt-0.5" style={{ color: theme.inkSoft }}>{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2.5">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} style={{ color: theme.inkSoft }} />
        <input
          type="text"
          placeholder="Buscar paciente, consulta…"
          className={`pl-9 pr-3 py-1.5 text-[12px] ${theme.bg} rounded-md w-64 focus:outline-none placeholder:opacity-60`}
          style={{ borderColor: theme.border, border: `1px solid ${theme.border}`, color: theme.ink }}
        />
      </div>
      <button className="p-1.5 rounded-md transition-colors relative" style={{ color: theme.inkSoft }}>
        <Bell className="w-4 h-4" strokeWidth={1.5} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: theme.accent }}></span>
      </button>
    </div>
  </header>
);

// =================== AGENDA MOCKUP (most representative) ===================
const AgendaScreen = ({ theme }) => {
  const consultas = [
    { hora: '09:00', nome: 'Beatriz Mendes', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: 'R$ 320' },
    { hora: '10:30', nome: 'Carla Souza', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal' },
    { hora: '14:00', nome: 'Helena Martins', tipo: 'retorno', status: 'confirmada', proc: 'Retorno' },
    { hora: '15:30', nome: 'Mariana Dias', tipo: 'presencial', status: 'realizada', proc: 'Preventivo', valor: 'R$ 250' },
  ];

  return (
    <div className={`flex h-full ${theme.bg} ${theme.font}`}>
      <Sidebar active="agenda" theme={theme} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Quinta · 30 de abril, 2026" theme={theme} />
        <main className="flex-1 overflow-y-auto p-7">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 text-[12px] font-medium rounded-md border" style={{ borderColor: theme.border, color: theme.ink, background: theme.surface }}>Hoje</button>
              <button className="p-1 rounded-md" style={{ color: theme.inkSoft }}><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/></button>
              <button className="p-1 rounded-md" style={{ color: theme.inkSoft }}><ChevronRight className="w-4 h-4" strokeWidth={1.5}/></button>
              <div className="w-px h-5 mx-1" style={{ background: theme.border }}></div>
              <h2 className="text-[15px] font-semibold tracking-tight" style={{ color: theme.ink }}>Abr 23 → Abr 25</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex p-0.5 rounded-md" style={{ background: theme.borderSoft }}>
                <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ background: theme.surface, color: theme.ink, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>Semana</button>
                <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: theme.inkSoft }}>Dia</button>
              </div>
              <button className="px-3 py-1.5 rounded-md text-[12px] font-medium flex items-center gap-1.5" style={{ background: theme.primary, color: theme.accentInk }}>
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5}/>
                Nova Consulta
              </button>
            </div>
          </div>

          {/* Vertical timeline-style list (mais moderno, sem grid de calendar) */}
          <div className={`${theme.surface} rounded-xl border overflow-hidden`} style={{ borderColor: theme.border }}>
            <div className="grid grid-cols-2 border-b" style={{ borderColor: theme.border }}>
              {[
                { label: 'QUI 23', count: '4 consultas', active: true },
                { label: 'SEX 24', count: '3 consultas' },
              ].map((d, i) => (
                <div key={i} className={`px-5 py-3 ${i === 0 ? 'border-r' : ''}`} style={{ borderColor: theme.border, background: d.active ? theme.borderSoft : 'transparent' }}>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: theme.inkSoft }}>{d.label}</div>
                  <div className="text-[13px] font-semibold mt-0.5" style={{ color: theme.ink }}>{d.count}</div>
                </div>
              ))}
            </div>

            <div>
              {consultas.map((c, i) => {
                const tipoCfg = theme.tipo[c.tipo];
                const stCfg = theme.status[c.status];
                return (
                  <div key={i} className="flex items-stretch border-b transition-colors hover:bg-black/[0.015] cursor-pointer group" style={{ borderColor: theme.borderSoft }}>
                    {/* Time column */}
                    <div className="px-5 py-4 flex items-start gap-3 border-r" style={{ borderColor: theme.borderSoft, width: 110 }}>
                      <div className="font-mono text-[13px] font-medium tabular-nums" style={{ color: theme.ink }}>{c.hora}</div>
                    </div>

                    {/* Type stripe (1px line, ultra subtle) */}
                    <div className={`w-0.5 ${tipoCfg.stripe}`}></div>

                    {/* Content */}
                    <div className="flex-1 px-5 py-4 flex items-center gap-4">
                      <Avatar name={c.nome} theme={theme} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[13px] font-semibold truncate" style={{ color: theme.ink }}>{c.nome}</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: theme.inkSoft }}>{tipoCfg.label.toUpperCase ? tipoCfg.label.toUpperCase() : tipoCfg.label}</span>
                        </div>
                        <div className="text-[11px]" style={{ color: theme.inkSoft }}>{c.proc}</div>
                      </div>

                      {/* Status as dot + text */}
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: stCfg.dot }}></span>
                        <span className={`text-[10px] uppercase tracking-wider font-bold ${stCfg.label}`}>
                          {c.status === 'confirmada' ? 'Confirmada' : c.status === 'agendada' ? 'Pendente' : c.status === 'realizada' ? 'Realizada' : 'Cancelada'}
                        </span>
                      </div>

                      {/* Valor */}
                      {c.valor && (
                        <span className="font-mono text-[12px] font-semibold tabular-nums w-16 text-right" style={{ color: theme.ink }}>{c.valor}</span>
                      )}
                      {!c.valor && <span className="w-16"></span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-3 flex items-center justify-between text-[11px]" style={{ color: theme.inkSoft }}>
              <span>4 consultas · R$ 570 previsto</span>
              <button className="flex items-center gap-1 hover:underline">Ver mais <ChevronRight className="w-3 h-3" strokeWidth={1.5}/></button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// =================== DASHBOARD MOCKUP ===================
const DashboardScreen = ({ theme }) => (
  <div className={`flex h-full ${theme.bg} ${theme.font}`}>
    <Sidebar active="dashboard" theme={theme} />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Bom dia, Nathalie." subtitle="quinta · 30 abr · 8 consultas hoje" theme={theme} />
      <main className="flex-1 overflow-y-auto p-7 space-y-5">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Consultas hoje', val: '8', delta: '+2' },
            { label: 'Pacientes ativos', val: '342', delta: '+12 este mês' },
            { label: 'Receita do mês', val: 'R$ 28.450', delta: '+12.4%' },
          ].map((m, i) => (
            <div key={i} className={`${theme.surface} border rounded-xl p-5`} style={{ borderColor: theme.border }}>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: theme.inkSoft }}>{m.label}</div>
              <div className="font-mono text-3xl font-semibold tracking-tight tabular-nums" style={{ color: theme.ink }}>{m.val}</div>
              <div className="flex items-center gap-1 mt-2 text-[11px]" style={{ color: theme.inkSoft }}>
                <TrendingUp className="w-3 h-3" strokeWidth={1.5}/>
                {m.delta}
              </div>
            </div>
          ))}
        </div>

        <div className={`${theme.surface} border rounded-xl overflow-hidden`} style={{ borderColor: theme.border }}>
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: theme.border }}>
            <div>
              <div className={`${theme.font} text-[14px] font-semibold tracking-tight`} style={{ color: theme.ink }}>Próximas consultas</div>
              <div className="text-[11px]" style={{ color: theme.inkSoft }}>5 confirmadas · 3 pendentes</div>
            </div>
            <button className="text-[11px] font-medium hover:underline" style={{ color: theme.ink }}>Ver agenda →</button>
          </div>
          <div>
            {[
              { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: 'R$ 320' },
              { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal' },
              { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta' },
              { hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'confirmada', proc: 'Preventivo', valor: 'R$ 250' },
            ].map((c, i) => {
              const stCfg = theme.status[c.status];
              const tipoCfg = theme.tipo[c.tipo];
              return (
                <div key={i} className="flex items-center gap-4 px-5 py-3 border-b last:border-b-0" style={{ borderColor: theme.borderSoft }}>
                  <div className="font-mono text-[12px] tabular-nums w-12" style={{ color: theme.ink }}>{c.hora}</div>
                  <div className={`w-0.5 self-stretch ${tipoCfg.stripe}`}></div>
                  <Avatar name={c.nome} theme={theme} size="md"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: theme.ink }}>{c.nome}</div>
                    <div className="text-[11px]" style={{ color: theme.inkSoft }}>{c.proc}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: stCfg.dot }}></span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${stCfg.label}`}>
                      {c.status === 'confirmada' ? 'Confirmada' : c.status === 'agendada' ? 'Pendente' : 'Realizada'}
                    </span>
                  </div>
                  {c.valor ? <span className="font-mono text-[12px] tabular-nums w-16 text-right" style={{ color: theme.ink }}>{c.valor}</span> : <span className="w-16"></span>}
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  </div>
);

// =================== MOBILE MOCKUP ===================
const MobileScreen = ({ theme }) => (
  <div className={`${theme.bg} ${theme.font} h-full overflow-y-auto`}>
    <header className={`sticky top-0 ${theme.surface} border-b px-5 py-4 z-10`} style={{ borderColor: theme.border }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: theme.inkSoft }}>QUI · 30 ABR</div>
          <h1 className={`${theme.font} text-xl font-semibold tracking-tight mt-0.5`} style={{ color: theme.ink }}>Agenda</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: theme.borderSoft, color: theme.ink }}><Search className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
          <button className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: theme.primary, color: theme.accentInk }}><Plus className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-1">
        {['28', '29', '30', '01', '02'].map((d, i) => {
          const isToday = i === 2;
          const dayLabel = ['SEG', 'TER', 'QUI', 'SEX', 'SÁB'][i];
          return (
            <button key={i}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg`}
              style={isToday ? { background: theme.primary, color: theme.accentInk } : { color: theme.inkSoft }}
            >
              <span className="text-[8px] uppercase tracking-[0.15em] font-bold">{dayLabel}</span>
              <span className={`font-mono text-[14px] font-semibold tabular-nums`} style={{ color: isToday ? theme.accentInk : theme.ink }}>{d}</span>
            </button>
          );
        })}
      </div>
    </header>

    <div className="p-4 space-y-2.5">
      {[
        { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: 'R$ 320' },
        { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal' },
        { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta' },
      ].map((c, i) => {
        const stCfg = theme.status[c.status];
        const tipoCfg = theme.tipo[c.tipo];
        return (
          <div key={i} className={`${theme.surface} border rounded-xl flex overflow-hidden`} style={{ borderColor: theme.border }}>
            <div className={`w-1 ${tipoCfg.stripe}`}></div>
            <div className="flex-1 p-3.5">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[12px] font-semibold tabular-nums" style={{ color: theme.ink }}>{c.hora}</span>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: stCfg.dot }}></span>
                  <span className={`text-[9px] uppercase tracking-wider font-bold ${stCfg.label}`}>
                    {c.status === 'confirmada' ? 'Confirmada' : c.status === 'agendada' ? 'Pendente' : 'Realizada'}
                  </span>
                </div>
              </div>
              <div className="text-[14px] font-semibold tracking-tight" style={{ color: theme.ink }}>{c.nome}</div>
              <div className="text-[11px] mt-0.5" style={{ color: theme.inkSoft }}>{c.proc}</div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: theme.inkSoft }}>{tipoCfg.label.toUpperCase ? tipoCfg.label.toUpperCase() : tipoCfg.label}</span>
                {c.valor && <span className="ml-auto font-mono text-[11px] tabular-nums" style={{ color: theme.ink }}>{c.valor}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    <nav className={`fixed bottom-0 left-0 right-0 ${theme.surface} border-t px-2 py-2 flex items-center justify-around max-w-[420px] mx-auto`} style={{ borderColor: theme.border }}>
      {[
        { icon: LayoutDashboard, label: 'Início' },
        { icon: Calendar, label: 'Agenda', active: true },
        { icon: Users, label: 'Pacientes' },
        { icon: Settings, label: 'Mais' },
      ].map((it, i) => {
        const Icon = it.icon;
        return (
          <button key={i} className="flex flex-col items-center gap-0.5 px-3 py-1.5"
            style={{ color: it.active ? theme.ink : theme.inkSoft }}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5}/>
            <span className="text-[8px] uppercase tracking-[0.15em] font-bold">{it.label}</span>
          </button>
        );
      })}
    </nav>
  </div>
);

// =================== PAGE ===================
export default function AppMockupsV2Page() {
  const [theme, setTheme] = useState('A');
  const [screen, setScreen] = useState('agenda');

  const themes = { A: THEME_A, B: THEME_B, C: THEME_C };
  const currentTheme = themes[theme];

  const Screen = screen === 'agenda' ? AgendaScreen : screen === 'dashboard' ? DashboardScreen : MobileScreen;
  const isMobile = screen === 'mobile';

  return (
    <>
      {FONT_LINK}
      <div className="min-h-screen bg-slate-100 p-6">
        <div className="max-w-[1500px] mx-auto">
          <header className="mb-5">
            <h1 className="font-geist text-3xl font-semibold tracking-tight text-slate-900">Premium Modern Minimal — 3 Propostas</h1>
            <p className="text-sm text-slate-500 mt-1">Cores restritas · alto contraste · tipografia moderna · sem visual "AI slop"</p>
          </header>

          {/* Theme tabs */}
          <div className="flex gap-2 mb-3">
            {Object.entries(themes).map(([k, t]) => (
              <button key={k}
                onClick={() => setTheme(k)}
                className={`px-4 py-3 text-left rounded-xl border transition-all flex-1 ${theme === k ? 'border-slate-900 bg-white shadow-md' : 'border-slate-200 bg-white/70 hover:border-slate-300'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Tema {k}</span>
                  <div className="flex gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: t.primary }}></span>
                    <span className="w-3 h-3 rounded-full" style={{ background: t.accent }}></span>
                    <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: t.bg.includes('FAF') ? '#FAFAF9' : '#FAFAFA' }}></span>
                  </div>
                </div>
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
              </button>
            ))}
          </div>

          {/* Screen tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { k: 'agenda', label: 'Agenda (Desktop)' },
              { k: 'dashboard', label: 'Dashboard (Desktop)' },
              { k: 'mobile', label: 'Mobile' },
            ].map(s => (
              <button key={s.k} onClick={() => setScreen(s.k)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${screen === s.k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Frame */}
          <div className={`rounded-2xl overflow-hidden border-8 border-slate-800 shadow-2xl ${isMobile ? 'mx-auto bg-black' : 'bg-slate-100'}`}
            style={isMobile ? { width: 420, height: 800 } : { height: 720 }}
          >
            <Screen theme={currentTheme} />
          </div>

          <p className="text-xs text-slate-400 mt-4 text-center">Todas as variantes mantêm 100% das funcionalidades atuais. Apenas estilo muda.</p>
        </div>
      </div>
    </>
  );
}
